import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Car,
  Plus,
  Phone,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  FileText
} from 'lucide-react';

const DriverManagement = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    vehicle_type: 'Wheelchair Van',
    license_plate: '',
    license_expiration: '',
    insurance_expiration: '',
    certifications: [],
    status: 'Available',
    is_active: true
  });

  const fetchDrivers = React.useCallback(async () => {
    try {
      const response = await authFetch(`/api/drivers`);
      const data = await response.json();
      setDrivers(data);
    } catch (err) {
      console.error('[DriverManagement] Failed to fetch drivers:', err);
      // TODO: Add user notification toast
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrivers();
  }, [navigate, fetchDrivers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingDriver
        ? `/api/drivers/${editingDriver.id}`
        : `/api/drivers`;

      const response = await authFetch(url, {
        method: editingDriver ? 'PUT' : 'POST',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowModal(false);
        setEditingDriver(null);
        resetForm();
        fetchDrivers();
      }
    } catch (err) {
      console.error('Failed to save driver:', err);
    }
  };

  const handleDelete = async (driverId) => {
    if (!window.confirm('Are you sure you want to delete this driver?')) return;
    
    try {
      const response = await authFetch(`/api/drivers/${driverId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        fetchDrivers();
      }
    } catch (err) {
      console.error('Failed to delete driver:', err);
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      name: driver.name,
      phone: driver.phone,
      vehicle_type: driver.vehicle_type,
      license_plate: driver.license_plate || '',
      license_expiration: driver.license_expiration || '',
      insurance_expiration: driver.insurance_expiration || '',
      certifications: driver.certifications || [],
      status: driver.status,
      is_active: driver.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      vehicle_type: 'Wheelchair Van',
      license_plate: '',
      license_expiration: '',
      insurance_expiration: '',
      certifications: [],
      status: 'Available',
      is_active: true
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      'Available': 'bg-green-100 text-green-800',
      'On Trip': 'bg-blue-100 text-blue-800',
      'Off Duty': 'bg-gray-100 text-gray-800',
      'Unavailable': 'bg-red-100 text-red-800'
    };
    return config[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F9A825] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading drivers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#004C97] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/dispatch/dashboard')}
                variant="ghost"
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl font-bold text-white">Driver Management</h1>
              <Badge variant="secondary" className="bg-white text-[#004C97]">
                {drivers.length} Total Drivers
              </Badge>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setEditingDriver(null);
                setShowModal(true);
              }}
              className="bg-[#F9A825] hover:bg-[#E08E1E] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Driver
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Available</p>
                  <p className="text-3xl font-bold text-green-600">
                    {drivers.filter(d => d.status === 'Available').length}
                  </p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">On Trip</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {drivers.filter(d => d.status === 'On Trip').length}
                  </p>
                </div>
                <Car className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Off Duty</p>
                  <p className="text-3xl font-bold text-gray-600">
                    {drivers.filter(d => d.status === 'Off Duty').length}
                  </p>
                </div>
                <Clock className="w-10 h-10 text-gray-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Active</p>
                  <p className="text-3xl font-bold text-[#004C97]">
                    {drivers.filter(d => d.is_active).length}
                  </p>
                </div>
                <FileText className="w-10 h-10 text-[#004C97]" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Drivers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {drivers.map((driver) => (
            <Card key={driver.id} className="border-2 hover:border-[#F9A825] transition-all">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg text-[#004C97]">{driver.name}</CardTitle>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold mt-2 ${getStatusBadge(driver.status)}`}>
                      {driver.status}
                    </span>
                  </div>
                  <div className="w-12 h-12 bg-[#F9A825] rounded-full flex items-center justify-center">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{driver.phone}</span>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Vehicle</p>
                  <p className="text-sm font-semibold text-[#004C97]">{driver.vehicle_type}</p>
                  {driver.license_plate && (
                    <p className="text-xs text-gray-600 mt-1">Plate: {driver.license_plate}</p>
                  )}
                </div>

                {driver.certifications && driver.certifications.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Certifications</p>
                    <div className="flex flex-wrap gap-1">
                      {driver.certifications.map((cert) => (
                        <span key={cert} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex space-x-2 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(driver)}
                    className="flex-1"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(driver.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {drivers.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Drivers Yet</h3>
              <p className="text-gray-600 mb-4">Add your first driver to get started</p>
              <Button
                onClick={() => setShowModal(true)}
                className="bg-[#F9A825] hover:bg-[#E08E1E] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Driver
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <Card className="w-full max-w-2xl border-2 border-[#F9A825] my-8">
            <CardHeader>
              <CardTitle className="text-[#004C97]">
                {editingDriver ? 'Edit Driver' : 'Add New Driver'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Phone *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Vehicle Type *</label>
                    <select
                      value={formData.vehicle_type}
                      onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    >
                      <option>Wheelchair Van</option>
                      <option>Ambulatory Van</option>
                      <option>Sedan</option>
                      <option>Stretcher Van</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">License Plate</label>
                    <input
                      type="text"
                      value={formData.license_plate}
                      onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">License Expiration</label>
                    <input
                      type="date"
                      value={formData.license_expiration}
                      onChange={(e) => setFormData({ ...formData, license_expiration: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Insurance Expiration</label>
                    <input
                      type="date"
                      value={formData.insurance_expiration}
                      onChange={(e) => setFormData({ ...formData, insurance_expiration: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Status *</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    >
                      <option>Available</option>
                      <option>On Trip</option>
                      <option>Off Duty</option>
                      <option>Unavailable</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                      Active Driver
                    </label>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-[#F9A825] hover:bg-[#E08E1E] text-white"
                  >
                    {editingDriver ? 'Update Driver' : 'Add Driver'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingDriver(null);
                      resetForm();
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DriverManagement;
